import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Card,
  CardContent,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Switch,
  Button,
  TextField,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import StorageIcon from '@mui/icons-material/Storage';
import ChatIcon from '@mui/icons-material/Chat';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import settingsApi from '../../api/settingsApi';
import knowledgeBaseApi from '../../api/knowledgeBaseApi';

const AdminSettingsPage = () => {
  // 브라우저 언어 자동 인식 ON/OFF
  const [browserLanguageDetection, setBrowserLanguageDetection] = useState(true);
  // 채팅 초기 인사말
  const [chatGreeting, setChatGreeting] = useState('안녕하세요, 어떤 것을 도와드릴까요?');
  // 상담 KB 선택
  const [availableKBs, setAvailableKBs] = useState([]);
  const [chatKbIds, setChatKbIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null); // 'success' | 'error' | null
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const [settings, kbResponse] = await Promise.all([
        settingsApi.getAllSettings(),
        knowledgeBaseApi.listKnowledgeBases().catch(() => ({ knowledge_bases: [] })),
      ]);

      const browserSetting = settings.find((s) => s.key === 'browser_language_detection');
      if (browserSetting) {
        setBrowserLanguageDetection(browserSetting.value === 'true');
        setUpdatedAt(browserSetting.updated_at);
      }

      const greetingSetting = settings.find((s) => s.key === 'chat_greeting');
      if (greetingSetting) {
        setChatGreeting(greetingSetting.value);
      }

      const kbSetting = settings.find((s) => s.key === 'chat_kb_ids');
      let savedKbIds = [];
      if (kbSetting) {
        try {
          savedKbIds = JSON.parse(kbSetting.value);
        } catch (_) {
          savedKbIds = [];
        }
      }

      const readyKBs = (kbResponse.knowledge_bases || []).filter((kb) => kb.status === 'ready');
      setAvailableKBs(readyKBs);
      setChatKbIds(savedKbIds);
    } catch (err) {
      console.error('설정 조회 오류:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKbToggle = (kbId) => {
    setChatKbIds((prev) =>
      prev.includes(kbId) ? prev.filter((id) => id !== kbId) : [...prev, kbId]
    );
    setSaveResult(null);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveResult(null);
      await Promise.all([
        settingsApi.updateSetting('browser_language_detection', browserLanguageDetection ? 'true' : 'false'),
        settingsApi.updateSetting('chat_kb_ids', JSON.stringify(chatKbIds)),
        settingsApi.updateSetting('chat_greeting', chatGreeting.trim() || '안녕하세요, 어떤 것을 도와드릴까요?'),
      ]);
      // localStorage 캐시도 즉시 업데이트
      localStorage.setItem('sys_browser_language_detection', browserLanguageDetection ? 'true' : 'false');
      setSaveResult('success');
      setUpdatedAt(new Date().toLocaleString('ko-KR'));
    } catch (err) {
      console.error('설정 저장 오류:', err);
      setSaveResult('error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4">시스템 설정</Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {saveResult === 'success' && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSaveResult(null)}>
              <AlertTitle>저장 완료</AlertTitle>
              설정이 성공적으로 저장되었습니다. 사용자가 다음 페이지 로드 시부터 반영됩니다.
            </Alert>
          )}
          {saveResult === 'error' && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveResult(null)}>
              <AlertTitle>저장 실패</AlertTitle>
              설정 저장 중 오류가 발생했습니다. 다시 시도해주세요.
            </Alert>
          )}

          {/* 언어 설정 카드 */}
          <Paper elevation={3} sx={{ mb: 3 }}>
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon color="primary" />
              <Typography variant="h6">언어 설정</Typography>
            </Box>
            <Divider />

            <Box sx={{ p: 3 }}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 240 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        웹브라우저 언어 자동 인식
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        사용자 화면(AI 질의응답)의 초기 UI 언어를 웹브라우저 설정에서 자동으로 감지합니다.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        <Chip
                          size="small"
                          label="ON: 브라우저 언어로 표시 (en-US → 영어)"
                          color={browserLanguageDetection ? 'primary' : 'default'}
                          variant={browserLanguageDetection ? 'filled' : 'outlined'}
                        />
                        <Chip
                          size="small"
                          label="OFF: 항상 기본값 한국어로 표시"
                          color={!browserLanguageDetection ? 'primary' : 'default'}
                          variant={!browserLanguageDetection ? 'filled' : 'outlined'}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={browserLanguageDetection}
                            onChange={(e) => {
                              setBrowserLanguageDetection(e.target.checked);
                              setSaveResult(null);
                            }}
                            color="primary"
                          />
                        }
                        label={browserLanguageDetection ? 'ON' : 'OFF'}
                        labelPlacement="start"
                      />
                    </Box>
                  </Box>

                  {updatedAt && (
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block' }}>
                      마지막 변경: {updatedAt}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>OFF 권장 상황:</strong> 한국 서비스인데 Chrome을 영어로 설정한 사용자가 영어 화면을 보는 경우.
                  OFF로 설정하면 브라우저 언어와 무관하게 항상 <strong>한국어</strong>로 시작합니다.
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  ※ 어느 설정이든, 사용자가 질문을 입력하면 그 언어를 감지하여 <strong>답변은 질문 언어로 자동 제공</strong>됩니다.
                </Typography>
              </Alert>
            </Box>
          </Paper>

          {/* 채팅 초기 인사말 카드 */}
          <Paper elevation={3} sx={{ mb: 3 }}>
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChatIcon color="primary" />
              <Typography variant="h6">채팅 초기 인사말</Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 3 }}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    사용자 채팅 시작 메시지
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    사용자가 질의응답 페이지에 처음 접속할 때 표시되는 AI 상담사의 첫 메시지입니다.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={5}
                    value={chatGreeting}
                    onChange={(e) => { setChatGreeting(e.target.value); setSaveResult(null); }}
                    placeholder="안녕하세요, 어떤 것을 도와드릴까요?"
                    inputProps={{ maxLength: 300 }}
                    helperText={`${chatGreeting.length}/300자`}
                  />
                </CardContent>
              </Card>
            </Box>
          </Paper>

          {/* 상담 KB 설정 카드 */}
          <Paper elevation={3} sx={{ mb: 3 }}>
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <StorageIcon color="primary" />
              <Typography variant="h6">상담 KB 설정</Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 3 }}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    사용자 질의응답에 사용할 지식베이스
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    체크된 지식베이스만 사용자 채팅(/user-chat)에서 검색합니다.
                    체크 항목이 없으면 첫 번째 KB가 자동 선택됩니다.
                  </Typography>
                  {availableKBs.length === 0 ? (
                    <Typography variant="body2" color="text.disabled">
                      준비(ready) 상태의 지식베이스가 없습니다.
                    </Typography>
                  ) : (
                    <FormGroup>
                      {availableKBs.map((kb) => (
                        <FormControlLabel
                          key={kb.kb_id}
                          control={
                            <Checkbox
                              checked={chatKbIds.includes(kb.kb_id)}
                              onChange={() => handleKbToggle(kb.kb_id)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {kb.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {kb.kb_id}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                </CardContent>
              </Card>
              <Alert severity="info" icon={<InfoOutlinedIcon />}>
                <Typography variant="body2">
                  복수 선택 시 모든 선택된 KB에서 동시에 검색하여 최적의 답변을 제공합니다.
                </Typography>
              </Alert>
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? '저장 중...' : '설정 저장'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AdminSettingsPage;
